import { FC, PropsWithChildren, useLayoutEffect, useState } from 'react'

import lightStyles from '@/theme/scss/palette.light.module.scss'
import darkStyles from '@/theme/scss/palette.dark.module.scss'
import { getLocalStorageItem, setLocalStorageItem } from '@/helpers/localStorage'
import { ThemeContext } from './ThemeContext'
import { Theme } from './types'

const THEME_KEY = 'CL/THEME'

export const ThemeProvider: FC<PropsWithChildren> = ({ children }) => {
    const [theme, setTheme] = useState<Theme>(Theme.light)

    const setDark = () => {
        document.body.classList.remove(lightStyles.light)
        document.body.classList.add(darkStyles.dark)
        setTheme(Theme.dark)
    }

    const setLight = () => {
        document.body.classList.remove(darkStyles.dark)
        document.body.classList.add(lightStyles.light)
        setTheme(Theme.light)
    }

    const setSystem = () => {
        document.body.classList.remove(darkStyles.dark)
        document.body.classList.remove(lightStyles.light)

        if (window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches) {
            setDark()
        } else {
            setLight()
        }

        setTheme(Theme.system)
    }

    const initializeTheme = () => {
        const theme = getLocalStorageItem(THEME_KEY)

        if (theme === Theme.dark) {
            setDark()
            return
        }

        if (theme === Theme.system) {
            setSystem()
            return
        }

        setLight()
    }

    const persistTheme = (theme: Theme) => {
        document.body.classList.remove(lightStyles.light)
        document.body.classList.remove(darkStyles.dark)

        if (theme === Theme.light) {
            setLight()
            setLocalStorageItem<Theme>(THEME_KEY, Theme.light)
            return
        }

        if (theme === Theme.dark) {
            setDark()
            setLocalStorageItem<Theme>(THEME_KEY, Theme.dark)
            return
        }

        if (theme === Theme.system) {
            setSystem()
            setLocalStorageItem<Theme>(THEME_KEY, Theme.system)
            return
        }
    }

    useLayoutEffect(() => {
        initializeTheme()
    }, [])

    return (
        <ThemeContext.Provider
            value={{
                theme,
                setTheme: persistTheme,
            }}
        >
            {children}
        </ThemeContext.Provider>
    )
}
